import React from "react";
import { useNavigate } from "react-router-dom";
import CloseIcon from "../../../components/icons/CloseIcon";
import FullScreenDialog from "../../common/FullScreenDialog";
import Header from "../../layout/components/Header";
import TenantLogo from "../../layout/components/TenantLogo";
import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { styled, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { setCurrentFAQsKey } from "../../../redux/slice/hints/hintsSlice";

interface HintsDialogProps {
  open: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  /**
   * Show the FAQ sub-navigation back arrow when a FAQ section is open.
   *
   * On by default, which is every hints route. The booking flow's in-place
   * terms overlay turns it off: `hints.currentFAQsKey` is global state left
   * behind by whatever FAQ section the customer last opened, so the arrow can
   * appear on a page that has nothing to go back to — and on the terms sheet
   * the only control should be the cross.
   */
  showFaqsBack?: boolean;
}

const BackText = styled(Typography)({
    color: 'black',
});

const HintsDialog = ({
  onClose,
  open,
  children,
  showFaqsBack = true,
}: HintsDialogProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentFAQsKey = useSelector((state: RootState) => state.hints.currentFAQsKey);

  // return to previous page if no onClose function is provided
  if (!onClose) onClose = () => navigate(-1);

  return (
    <FullScreenDialog open={open} onClose={onClose} ariaLabel={t("common.dialog.hints")}>
      <Header>
        <div className="h-12 absolute left-2 flex items-center">
          {showFaqsBack && currentFAQsKey !== "" && 
            <button
              className="flex items-center text-sm text-foreground/90"
              onClick={() => dispatch(setCurrentFAQsKey(""))}
            >
              <ChevronLeftIcon className="size-8 text-black-500" />
              <BackText>{t('general.back')}</BackText>
            </button>
          }
        </div>
        <TenantLogo />
        <button
          className="size-12 absolute right-0 text-foreground/90"
          onClick={onClose}
        >
          <CloseIcon />
        </button>
      </Header>
      <div className="px-5 max-w-full">{children}</div>
    </FullScreenDialog>
  );
};

export default HintsDialog;
